/**
 * Vendor Service
 * Manage church vendors/suppliers for expenses and purchases
 */

import { supabase } from '@/lib/supabase'

export interface Vendor {
  id: string
  church_id: string
  vendor_code: string
  vendor_name: string
  vendor_type: 'supplier' | 'contractor' | 'service_provider' | 'utility' | 'other'

  // Contact
  contact_person?: string
  email?: string
  phone?: string
  mobile?: string
  website?: string

  // Address
  address_line1?: string
  address_line2?: string
  city?: string
  state_province?: string
  postal_code?: string
  country: string

  // Financial
  tax_id?: string
  payment_terms: number
  credit_limit: number
  current_balance: number

  // Accounting
  default_expense_account_id?: string
  payables_account_id?: string

  // Bank
  bank_name?: string
  bank_account_number?: string
  bank_branch?: string
  bank_swift_code?: string

  // Status
  status: 'active' | 'inactive' | 'suspended'
  is_active: boolean
  notes?: string

  // Audit
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
}

export interface VendorTransaction {
  id: string
  church_id: string
  vendor_id: string
  transaction_date: string
  transaction_type: 'purchase' | 'payment' | 'credit_note' | 'debit_note' | 'opening_balance'
  reference_number?: string
  debit_amount: number
  credit_amount: number
  balance: number
  expense_id?: string
  payment_id?: string
  journal_entry_id?: string
  description?: string
  created_at: string
  created_by?: string
}

export interface VendorSummary extends Vendor {
  total_transactions: number
  total_purchases: number
  last_transaction_date?: string
}

export class VendorService {
  /**
   * Get all vendors for a church
   */
  static async getVendors(churchId: string, filters?: {
    status?: string
    vendor_type?: string
    search?: string
  }): Promise<VendorSummary[]> {
    let query = supabase
      .from('v_vendor_summary')
      .select('*')
      .eq('church_id', churchId)
      .order('vendor_name')

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.vendor_type) {
      query = query.eq('vendor_type', filters.vendor_type)
    }

    if (filters?.search) {
      query = query.or(`vendor_name.ilike.%${filters.search}%,vendor_code.ilike.%${filters.search}%,email.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get a single vendor by ID
   */
  static async getVendor(vendorId: string): Promise<Vendor> {
    const { data, error } = await supabase
      .from('vendors')
      .select('*')
      .eq('id', vendorId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create a new vendor
   */
  static async createVendor(
    churchId: string,
    vendorData: Omit<Vendor, 'id' | 'vendor_code' | 'church_id' | 'current_balance' | 'created_at' | 'updated_at'>,
    userId: string
  ): Promise<Vendor> {
    console.log('VendorService.createVendor called')
    console.log('Church ID:', churchId)
    console.log('User ID:', userId)
    console.log('Vendor Data:', vendorData)

    // Generate vendor code
    console.log('Generating vendor code...')
    const { data: codeData, error: codeError } = await supabase.rpc('generate_vendor_code', {
      p_church_id: churchId
    })

    if (codeError) {
      console.error('Error generating vendor code:', codeError)
      throw codeError
    }

    console.log('Generated vendor code:', codeData)

    const insertData = {
      church_id: churchId,
      vendor_code: codeData,
      ...vendorData,
      created_by: userId,
      updated_by: userId
    }

    console.log('Inserting vendor with data:', insertData)

    const { data, error } = await supabase
      .from('vendors')
      .insert(insertData)
      .select()
      .single()

    if (error) {
      console.error('Error inserting vendor:', error)
      throw error
    }

    console.log('Vendor created successfully:', data)
    return data
  }

  /**
   * Update a vendor
   */
  static async updateVendor(
    vendorId: string,
    vendorData: Partial<Vendor>,
    userId: string
  ): Promise<Vendor> {
    // Remove fields that shouldn't be updated
    const {
      id,
      vendor_code,
      church_id,
      created_at,
      created_by,
      current_balance,
      ...updateData
    } = vendorData as any

    console.log('Updating vendor:', vendorId)
    console.log('Update data:', updateData)

    const { data, error } = await supabase
      .from('vendors')
      .update({
        ...updateData,
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', vendorId)
      .select()
      .single()

    if (error) {
      console.error('Error updating vendor:', error)
      throw error
    }

    console.log('Vendor updated successfully:', data)
    return data
  }

  /**
   * Delete a vendor (soft delete by setting inactive)
   */
  static async deleteVendor(vendorId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('vendors')
      .update({
        is_active: false,
        status: 'inactive',
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', vendorId)

    if (error) throw error
  }

  /**
   * Get vendor transactions
   */
  static async getVendorTransactions(
    vendorId: string,
    filters?: {
      start_date?: string
      end_date?: string
      transaction_type?: string
    }
  ): Promise<VendorTransaction[]> {
    let query = supabase
      .from('vendor_transactions')
      .select('*')
      .eq('vendor_id', vendorId)
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false })

    if (filters?.start_date) {
      query = query.gte('transaction_date', filters.start_date)
    }

    if (filters?.end_date) {
      query = query.lte('transaction_date', filters.end_date)
    }

    if (filters?.transaction_type) {
      query = query.eq('transaction_type', filters.transaction_type)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Record a vendor purchase (from expense)
   */
  static async recordPurchase(
    churchId: string,
    vendorId: string,
    expenseId: string,
    amount: number,
    transactionDate: string,
    description: string,
    userId: string
  ): Promise<VendorTransaction> {
    // Get current vendor balance
    const { data: vendor } = await supabase
      .from('vendors')
      .select('current_balance')
      .eq('id', vendorId)
      .single()

    const currentBalance = vendor?.current_balance || 0
    const newBalance = currentBalance + amount

    const { data, error } = await supabase
      .from('vendor_transactions')
      .insert({
        church_id: churchId,
        vendor_id: vendorId,
        transaction_date: transactionDate,
        transaction_type: 'purchase',
        debit_amount: amount,
        credit_amount: 0,
        balance: newBalance,
        expense_id: expenseId,
        description: description,
        created_by: userId
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Record a vendor payment
   */
  static async recordPayment(
    churchId: string,
    vendorId: string,
    amount: number,
    paymentDate: string,
    referenceNumber: string,
    description: string,
    journalEntryId: string,
    userId: string
  ): Promise<VendorTransaction> {
    // Get current vendor balance
    const { data: vendor } = await supabase
      .from('vendors')
      .select('current_balance')
      .eq('id', vendorId)
      .single()

    const currentBalance = vendor?.current_balance || 0
    const newBalance = currentBalance - amount

    const { data, error } = await supabase
      .from('vendor_transactions')
      .insert({
        church_id: churchId,
        vendor_id: vendorId,
        transaction_date: paymentDate,
        transaction_type: 'payment',
        reference_number: referenceNumber,
        debit_amount: 0,
        credit_amount: amount,
        balance: newBalance,
        journal_entry_id: journalEntryId,
        description: description,
        created_by: userId
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Get vendors with outstanding balances
   */
  static async getVendorsWithBalance(churchId: string): Promise<VendorSummary[]> {
    const { data, error } = await supabase
      .from('v_vendor_summary')
      .select('*')
      .eq('church_id', churchId)
      .gt('current_balance', 0)
      .order('current_balance', { ascending: false })

    if (error) throw error
    return data || []
  }

  /**
   * Get vendor aging report (accounts payable aging)
   */
  static async getVendorAgingReport(churchId: string, asOfDate: string): Promise<any[]> {
    // This would calculate aging buckets: 0-30, 31-60, 61-90, 90+ days
    // For now, returning basic data
    const { data, error } = await supabase
      .from('v_vendor_summary')
      .select('*')
      .eq('church_id', churchId)
      .gt('current_balance', 0)

    if (error) throw error
    return data || []
  }

  /**
   * Get vendor purchase summary report
   */
  static async getVendorPurchaseSummary(
    churchId: string,
    startDate: string,
    endDate: string,
    vendorId?: string
  ): Promise<any[]> {
    let query = supabase
      .from('vendor_transactions')
      .select(`
        vendor_id,
        vendors!inner(vendor_code, vendor_name, vendor_type),
        debit_amount
      `)
      .eq('church_id', churchId)
      .eq('transaction_type', 'purchase')
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate)

    if (vendorId) {
      query = query.eq('vendor_id', vendorId)
    }

    const { data, error } = await query

    if (error) throw error

    // Group by vendor
    const summary: Record<string, any> = {}
    data?.forEach(txn => {
      const vendorId = txn.vendor_id
      if (!summary[vendorId]) {
        summary[vendorId] = {
          vendor_id: vendorId,
          vendor_code: txn.vendors?.vendor_code,
          vendor_name: txn.vendors?.vendor_name,
          vendor_type: txn.vendors?.vendor_type,
          total_purchases: 0,
          transaction_count: 0
        }
      }
      summary[vendorId].total_purchases += txn.debit_amount
      summary[vendorId].transaction_count += 1
    })

    return Object.values(summary).sort((a, b) => b.total_purchases - a.total_purchases)
  }

  /**
   * Get vendor payment history report
   */
  static async getVendorPaymentHistory(
    churchId: string,
    startDate: string,
    endDate: string,
    vendorId?: string
  ): Promise<any[]> {
    let query = supabase
      .from('vendor_transactions')
      .select(`
        *,
        vendors!inner(vendor_code, vendor_name)
      `)
      .eq('church_id', churchId)
      .eq('transaction_type', 'payment')
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate)
      .order('transaction_date', { ascending: false })

    if (vendorId) {
      query = query.eq('vendor_id', vendorId)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get vendor outstanding invoices
   */
  static async getVendorOutstandingInvoices(
    churchId: string,
    vendorId?: string
  ): Promise<any[]> {
    let query = supabase
      .from('expenses')
      .select(`
        *,
        vendors(vendor_code, vendor_name)
      `)
      .eq('church_id', churchId)
      .not('vendor_id', 'is', null)
      .order('expense_date', { ascending: true })

    if (vendorId) {
      query = query.eq('vendor_id', vendorId)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get vendor statistics for dashboard
   */
  static async getVendorStatistics(churchId: string): Promise<{
    total_vendors: number
    active_vendors: number
    total_outstanding: number
    vendors_with_balance: number
    top_vendors_by_purchase: any[]
  }> {
    // Get all vendors summary
    const { data: vendors } = await supabase
      .from('v_vendor_summary')
      .select('*')
      .eq('church_id', churchId)

    const totalVendors = vendors?.length || 0
    const activeVendors = vendors?.filter(v => v.status === 'active').length || 0
    const totalOutstanding = vendors?.reduce((sum, v) => sum + (v.current_balance || 0), 0) || 0
    const vendorsWithBalance = vendors?.filter(v => v.current_balance > 0).length || 0

    // Get top vendors by purchase amount (last 12 months)
    const twelveMonthsAgo = new Date()
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12)

    const topVendors = await this.getVendorPurchaseSummary(
      churchId,
      twelveMonthsAgo.toISOString().split('T')[0],
      new Date().toISOString().split('T')[0]
    )

    return {
      total_vendors: totalVendors,
      active_vendors: activeVendors,
      total_outstanding: totalOutstanding,
      vendors_with_balance: vendorsWithBalance,
      top_vendors_by_purchase: topVendors.slice(0, 10)
    }
  }

  /**
   * Get vendor transaction detail report
   */
  static async getVendorTransactionReport(
    churchId: string,
    startDate: string,
    endDate: string,
    vendorId?: string,
    transactionType?: string
  ): Promise<any[]> {
    let query = supabase
      .from('vendor_transactions')
      .select(`
        *,
        vendors!inner(vendor_code, vendor_name, vendor_type)
      `)
      .eq('church_id', churchId)
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate)
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false })

    if (vendorId) {
      query = query.eq('vendor_id', vendorId)
    }

    if (transactionType) {
      query = query.eq('transaction_type', transactionType)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get vendor by type summary
   */
  static async getVendorByTypeSummary(churchId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('v_vendor_summary')
      .select('vendor_type, current_balance, total_purchases')
      .eq('church_id', churchId)

    if (error) throw error

    // Group by vendor type
    const summary: Record<string, any> = {}
    data?.forEach(vendor => {
      const type = vendor.vendor_type || 'other'
      if (!summary[type]) {
        summary[type] = {
          vendor_type: type,
          vendor_count: 0,
          total_outstanding: 0,
          total_purchases: 0
        }
      }
      summary[type].vendor_count += 1
      summary[type].total_outstanding += vendor.current_balance || 0
      summary[type].total_purchases += vendor.total_purchases || 0
    })

    return Object.values(summary).sort((a, b) => b.total_purchases - a.total_purchases)
  }
}

export default VendorService
